import { allowedMimeTypes } from "@/hooks/useFileRecordUpload";

/**
 * A native drag that STARTED inside the document — dragging an `<img>` (a
 * canvas card's hero, a list row's thumbnail) — also advertises "Files" in
 * `dataTransfer.types`, and Chrome even populates `files` with the image on
 * drop. Without this flag such a drag lights up every upload zone and can
 * attach the hero image to the composer. OS drags never fire an in-document
 * `dragstart`, so the flag is only ever true for internal drags; dnd-kit
 * drags are pointer-based and fire no native drag events at all. Cleared on
 * `dragend` (which fires on the in-document source after any drop/cancel)
 * with a `pointerdown` backstop in case the source unmounts mid-drag.
 */
let internalNativeDragActive = false;
if (typeof window !== "undefined") {
  const clear = () => {
    internalNativeDragActive = false;
  };
  window.addEventListener(
    "dragstart",
    () => {
      internalNativeDragActive = true;
    },
    true,
  );
  window.addEventListener("dragend", clear, true);
  window.addEventListener("pointerdown", clear, true);
}

/** True when the drag carries OS files (not dnd-kit or native in-app drags). */
export function isOsFileDrag(event: DragEvent): boolean {
  if (internalNativeDragActive) return false;
  return event.dataTransfer?.types.includes("Files") ?? false;
}

/**
 * `dragleave` fired because the pointer moved onto a child inside the drop
 * root — not because it left the zone. Without this guard, a depth counter
 * decrements and the overlay flickers off while the drag is still inside.
 */
export function isDragLeavingToDescendant(
  event: Pick<DragEvent, "relatedTarget" | "currentTarget">,
): boolean {
  const related = event.relatedTarget;
  const current = event.currentTarget;
  return (
    related instanceof Node &&
    current instanceof Node &&
    current.contains(related)
  );
}

/**
 * `dragenter` bubbled from entering via a descendant the root already
 * counted — skip a duplicate depth bump.
 */
export function isDragEnterBubbledFromDescendant(
  event: Pick<DragEvent, "relatedTarget" | "currentTarget">,
): boolean {
  const related = event.relatedTarget;
  const current = event.currentTarget;
  if (!(related instanceof Node) || !(current instanceof Node)) return false;
  return current.contains(related) && related !== current;
}

export function isFileTypeAllowed(file: File): boolean {
  const ext = file.name.split(".").pop()?.toLowerCase();
  if (ext && ext in allowedMimeTypes) return true;
  const mimeType = file.type;
  if (mimeType) {
    return Object.values(allowedMimeTypes).some(
      (allowed) => allowed === mimeType,
    );
  }
  return false;
}

export async function readFilesFromDataTransfer(
  dataTransfer: DataTransfer,
): Promise<File[]> {
  const files = Array.from(dataTransfer.files);
  return files.filter(isFileTypeAllowed);
}
